/**
 * Extraction limits shared by the upload pipeline and the Documents tab.
 *
 * Two different numbers, deliberately:
 *
 *  - `EXTRACTED_TEXT_MAX_CHARS` (10,000) is the PREVIEW budget. It caps
 *    the upload preview and the SED's `uploadedDocuments[]`. Source-backed
 *    site metadata uses the separate 1k budget below; legacy rows retain 10k.
 *    Roughly three to four pages of prose; the whole-SED prompt reads it.
 *  - `EXTRACTED_TEXT_HARD_MAX_CHARS` (2,000,000) is the BODY ceiling. The
 *    full extracted text (`fullExtractedText`, the per-document GCS JSON,
 *    the RAG chunks, section extraction) keeps everything up to it; only a
 *    document longer than that is cut, and then `truncated` is stamped.
 *
 * Before the document-intelligence plan (P1.2, 2026-09-07) the preview cap
 * was applied inside every extractor, so nothing past page 3 or so of any
 * upload ever existed anywhere in the system.
 *
 * Lives in a dependency-free module so UI code can explain the caps without
 * importing the (heavy, browser-only) upload service.
 */
export const EXTRACTED_TEXT_MAX_CHARS = 10_000;
/** Site metadata only; source-backed documents hydrate their complete body. */
export const SITE_DOCUMENT_PREVIEW_MAX_CHARS = 1_000;
export const EXTRACTED_TEXT_HARD_MAX_CHARS = 2_000_000;
/** PDF pages read for text (raised from 50 with P1.2); OCR keeps its own cap. */
export const PDF_TEXT_PAGE_CAP = 400;

/**
 * True when an extraction lost text. `truncated` is stamped by
 * `processUploadedFile`: on uploads made before P1.2 it means the 10k
 * preview was all that was kept; on later uploads it means the body ceiling
 * cut the document. `extractedCharsKept` says how much survived (absent on
 * pre-P1.2 uploads, where the answer is the preview cap).
 */
export function isExtractionTruncated(doc: {
  truncated?: boolean;
  extractedCharsTotal?: number;
}): boolean {
  return doc.truncated === true && typeof doc.extractedCharsTotal === 'number';
}

/** Characters of a document's body that were captured (legacy uploads: the preview cap). */
export function extractedCharsKept(doc: { extractedCharsKept?: number }): number {
  return typeof doc.extractedCharsKept === 'number' ? doc.extractedCharsKept : EXTRACTED_TEXT_MAX_CHARS;
}
